import { useCallback, useEffect, useState, useRef } from "react";
import axios from "axios";
import { Plus, Search, Eye, Pencil, Paperclip, Archive as ArchiveIcon, RotateCcw, Trash2, ShieldCheck, ListChecks, Clock, AlertTriangle } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Panel, EmptyState, formatDate } from "@/components/admin/ui";
import { usePolling } from "@/hooks/usePolling";
import { LastUpdated } from "@/components/common/LastUpdated";
import { CredentialAddModal } from "@/components/org/credentials/CredentialAddModal";
import { CredentialEditModal } from "@/components/org/credentials/CredentialEditModal";
import { CredentialDetailDrawer } from "@/components/org/credentials/CredentialDetailDrawer";
import {
  CREDENTIAL_TYPE_META, CREDENTIAL_TYPE_ORDER, CREDENTIAL_STATUS_STYLES, CREDENTIAL_SORT_OPTIONS, scopeReferenceLabel,
} from "@/lib/credentials";

const SUMMARY_CARDS = [
  { key: "total", label: "Total Credentials", icon: ListChecks, tone: "text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-500/10" },
  { key: "active", label: "Active", icon: ShieldCheck, tone: "text-emerald-600 bg-emerald-50 dark:text-emerald-400 dark:bg-emerald-500/10" },
  { key: "expiring_soon", label: "Expiring Soon", helper: "≤ 60 days", icon: Clock, tone: "text-amber-600 bg-amber-50 dark:text-amber-400 dark:bg-amber-500/10" },
  { key: "expired", label: "Expired", icon: AlertTriangle, tone: "text-red-600 bg-red-50 dark:text-red-400 dark:bg-red-500/10" },
];

export default function OrgCredentials() {
  const { API, user } = useAuth();

  const [meta, setMeta] = useState({ users: [], teams: [], service_areas: [], locations: [] });
  const [credentials, setCredentials] = useState(null);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);

  const [selectedType, setSelectedType] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [holderTypeFilter, setHolderTypeFilter] = useState("");
  const [sort, setSort] = useState("expiration_soonest");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const searchDebounce = useRef(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCredential, setEditingCredential] = useState(null);
  const [viewingCredentialId, setViewingCredentialId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // { id, type: "archive" | "restore" | "delete" }

  useEffect(() => {
    axios.get(`${API}/org/credentials/meta`, { withCredentials: true }).then(({ data }) => setMeta(data)).catch(() => {});
  }, [API]);

  useEffect(() => {
    clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(searchDebounce.current);
  }, [searchInput]);

  const load = useCallback(async (silent) => {
    try {
      const params = {};
      if (selectedType) params.credential_type = selectedType;
      if (statusFilter) params.status = statusFilter;
      if (holderTypeFilter) params.holder_type = holderTypeFilter;
      if (search) params.q = search;
      if (sort) params.sort = sort;
      const { data } = await axios.get(`${API}/org/credentials`, { params, withCredentials: true });
      setCredentials(data.credentials);
      setSummary(data.summary);
      setLastUpdated(Date.now());
    } catch (err) {
      if (!silent) setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, [API, selectedType, statusFilter, holderTypeFilter, search, sort]);

  useEffect(() => { load(); }, [load]);
  usePolling(() => {
    if (showAddModal || editingCredential || viewingCredentialId || busyId) return;
    load(true);
  }, 20000);

  const toggleType = (type) => setSelectedType((prev) => (prev === type ? "" : type));

  const runAction = async (id, action) => {
    setBusyId(id);
    try {
      if (action === "archive") await axios.post(`${API}/org/credentials/${id}/archive`, {}, { withCredentials: true });
      else if (action === "restore") await axios.post(`${API}/org/credentials/${id}/restore`, {}, { withCredentials: true });
      else if (action === "delete") await axios.delete(`${API}/org/credentials/${id}`, { withCredentials: true });
      setConfirmAction(null);
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const rows = credentials || [];
  const hasFilters = !!(selectedType || statusFilter || holderTypeFilter || search);

  return (
    <div className="space-y-6" data-testid="org-credentials-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-950 dark:text-white">Credentials</h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-500 dark:text-slate-400">
            Manage your organization's licenses, insurance, certifications, training, permits, and expertise.
            Keep your credentials up to date to support operational readiness and qualification requirements.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <LastUpdated timestamp={lastUpdated} />
          <button type="button" onClick={() => setShowAddModal(true)} data-testid="credential-add-btn" className="inline-flex items-center gap-2 rounded-xl bg-[#0052FF] px-4 py-2.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-blue-700">
            <Plus size={16} /> Add Credential
          </button>
        </div>
      </div>

      {error && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="org-credentials-error">{error}</p>}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="credential-summary-cards">
        {SUMMARY_CARDS.map(({ key, label, helper, icon: Icon, tone }) => (
          <div key={key} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-[#111C33]" data-testid={`credential-summary-${key}`}>
            <div className="flex items-center gap-4">
              <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${tone}`}><Icon size={21} strokeWidth={1.9} /></span>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">{label}</p>
                <p className="mt-1 text-3xl font-extrabold tracking-tight text-slate-950 dark:text-white">{summary ? summary[key] : "—"}</p>
              </div>
            </div>
            {helper && <p className="mt-4 text-xs font-semibold text-slate-500 dark:text-slate-400">{helper}</p>}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-3 sm:grid-cols-5 lg:grid-cols-9" data-testid="credential-category-cards">
        {CREDENTIAL_TYPE_ORDER.map((type) => {
          const { label, icon: Icon } = CREDENTIAL_TYPE_META[type];
          const active = selectedType === type;
          const count = summary?.by_type?.[type] ?? 0;
          return (
            <button
              key={type}
              type="button"
              onClick={() => toggleType(type)}
              data-testid={`credential-category-${type}`}
              className={`flex flex-col items-center gap-2 rounded-xl border p-3 text-center transition-colors duration-200 ${active ? "border-[#0052FF] bg-blue-50 dark:border-blue-400 dark:bg-blue-500/10" : "border-slate-200 bg-white hover:bg-slate-50 dark:border-white/10 dark:bg-[#111C33] dark:hover:bg-white/5"}`}
            >
              <Icon size={20} strokeWidth={1.8} className={active ? "text-[#0052FF] dark:text-blue-400" : "text-slate-500 dark:text-slate-400"} />
              <span className="text-xs font-bold text-slate-700 dark:text-slate-200">{label}</span>
              <span className="text-[11px] font-semibold text-slate-400 dark:text-slate-500">{count}</span>
            </button>
          );
        })}
      </div>

      <Panel testId="credential-list-panel">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search credentials…"
              data-testid="credential-search-input"
              className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-9 pr-3 text-sm font-semibold text-slate-900 dark:border-white/15 dark:bg-white/5 dark:text-white"
            />
          </div>
          <select value={selectedType} onChange={(e) => setSelectedType(e.target.value)} data-testid="credential-filter-type" className="rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold dark:border-white/15 dark:bg-white/5 dark:text-white">
            <option value="">All Types</option>
            {CREDENTIAL_TYPE_ORDER.map((t) => <option key={t} value={t}>{CREDENTIAL_TYPE_META[t].singular}</option>)}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="credential-filter-status" className="rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold dark:border-white/15 dark:bg-white/5 dark:text-white">
            <option value="">All Statuses</option>
            {Object.keys(CREDENTIAL_STATUS_STYLES).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={holderTypeFilter} onChange={(e) => setHolderTypeFilter(e.target.value)} data-testid="credential-filter-holder" className="rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold dark:border-white/15 dark:bg-white/5 dark:text-white">
            <option value="">All Holders</option>
            <option value="organization">Organization</option>
            <option value="person">Person</option>
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="credential-sort-select" className="rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold dark:border-white/15 dark:bg-white/5 dark:text-white">
            {CREDENTIAL_SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        <div className="mt-5">
          {rows.length === 0 ? (
            hasFilters ? (
              <EmptyState icon={Search} title="No credentials match your filters" message="Try a different search term or clear your filters." testId="credential-empty-filtered" />
            ) : (
              <EmptyState
                icon={ShieldCheck}
                title="No credentials yet"
                message="Add licenses, insurance, certifications, training, and other qualifications your organization uses."
                testId="credential-empty-state"
              />
            )
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm" data-testid="credential-table">
                <thead>
                  <tr className="border-b border-slate-200 text-xs font-bold uppercase tracking-wide text-slate-400 dark:border-white/10 dark:text-slate-500">
                    <th className="py-2 pr-4">Credential</th>
                    <th className="py-2 pr-4">Type</th>
                    <th className="py-2 pr-4">Number</th>
                    <th className="py-2 pr-4">Issued By</th>
                    <th className="py-2 pr-4">Holder</th>
                    <th className="py-2 pr-4">Issue Date</th>
                    <th className="py-2 pr-4">Expiration</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4">Scope</th>
                    <th className="py-2 pr-4">Docs</th>
                    <th className="py-2 pr-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                  {rows.map((c) => {
                    const typeMeta = CREDENTIAL_TYPE_META[c.credential_type];
                    return (
                      <tr key={c.credential_id} data-testid={`credential-row-${c.credential_id}`}>
                        <td className="py-3 pr-4">
                          <button type="button" onClick={() => setViewingCredentialId(c.credential_id)} data-testid={`credential-row-name-${c.credential_id}`} className="text-left font-bold text-slate-900 hover:text-[#0052FF] dark:text-white">
                            {c.credential_name}
                          </button>
                          {c.description && <p className="mt-0.5 max-w-xs truncate text-xs text-slate-500 dark:text-slate-400">{c.description}</p>}
                        </td>
                        <td className="py-3 pr-4"><span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${typeMeta.badge}`}>{typeMeta.singular}</span></td>
                        <td className="py-3 pr-4 text-slate-600 dark:text-slate-300">{c.credential_number || "—"}</td>
                        <td className="py-3 pr-4 text-slate-600 dark:text-slate-300">{c.issuer || "—"}</td>
                        <td className="py-3 pr-4 text-slate-600 dark:text-slate-300">{c.holder_name}</td>
                        <td className="py-3 pr-4 text-slate-600 dark:text-slate-300">{formatDate(c.issue_date)}</td>
                        <td className="py-3 pr-4 text-slate-600 dark:text-slate-300">{c.does_not_expire ? "—" : formatDate(c.expiration_date)}</td>
                        <td className="py-3 pr-4"><span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${CREDENTIAL_STATUS_STYLES[c.status] || ""}`}>{c.status}</span></td>
                        <td className="py-3 pr-4 text-slate-600 dark:text-slate-300">{scopeReferenceLabel(c, meta)}</td>
                        <td className="py-3 pr-4">
                          <button type="button" onClick={() => setViewingCredentialId(c.credential_id)} data-testid={`credential-row-docs-${c.credential_id}`} className="inline-flex items-center gap-1 text-slate-500 hover:text-[#0052FF] dark:text-slate-400">
                            <Paperclip size={13} /> {c.document_count}
                          </button>
                        </td>
                        <td className="py-3 pr-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button type="button" onClick={() => setViewingCredentialId(c.credential_id)} data-testid={`credential-row-view-${c.credential_id}`} title="View" className="text-slate-400 hover:text-slate-700 dark:hover:text-white"><Eye size={15} /></button>
                            {c.can_manage && (
                              <>
                                <button type="button" onClick={() => setEditingCredential(c)} data-testid={`credential-row-edit-${c.credential_id}`} title="Edit" className="text-slate-400 hover:text-slate-700 dark:hover:text-white"><Pencil size={15} /></button>
                                {confirmAction?.id === c.credential_id ? (
                                  <div className="flex items-center gap-1" data-testid={`credential-row-confirm-${c.credential_id}`}>
                                    <button type="button" onClick={() => runAction(c.credential_id, confirmAction.type)} disabled={busyId === c.credential_id} data-testid={`credential-row-confirm-yes-${c.credential_id}`} className="rounded bg-red-600 px-2 py-1 text-[11px] font-bold text-white disabled:opacity-50">Confirm</button>
                                    <button type="button" onClick={() => setConfirmAction(null)} data-testid={`credential-row-confirm-cancel-${c.credential_id}`} className="rounded border border-slate-200 px-2 py-1 text-[11px] font-bold text-slate-500 dark:border-white/15">Cancel</button>
                                  </div>
                                ) : c.status === "Archived" ? (
                                  <button type="button" onClick={() => setConfirmAction({ id: c.credential_id, type: "restore" })} data-testid={`credential-row-restore-${c.credential_id}`} title="Restore" className="text-slate-400 hover:text-emerald-600"><RotateCcw size={15} /></button>
                                ) : (
                                  <button type="button" onClick={() => setConfirmAction({ id: c.credential_id, type: "archive" })} data-testid={`credential-row-archive-${c.credential_id}`} title="Archive" className="text-slate-400 hover:text-amber-600"><ArchiveIcon size={15} /></button>
                                )}
                                <button type="button" onClick={() => setConfirmAction({ id: c.credential_id, type: "delete" })} data-testid={`credential-row-delete-${c.credential_id}`} title="Delete" className="text-slate-400 hover:text-red-600"><Trash2 size={15} /></button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Panel>

      {showAddModal && <CredentialAddModal meta={meta} currentUserId={user?.user_id} onClose={() => setShowAddModal(false)} onSaved={load} />}
      {editingCredential && (
        <CredentialEditModal
          credential={editingCredential}
          meta={meta}
          onClose={() => setEditingCredential(null)}
          onSaved={() => load()}
        />
      )}
      {viewingCredentialId && (
        <CredentialDetailDrawer
          credentialId={viewingCredentialId}
          meta={meta}
          onClose={() => setViewingCredentialId(null)}
          onChanged={load}
          onEdit={(cred) => { setViewingCredentialId(null); setEditingCredential(cred); }}
        />
      )}
    </div>
  );
}
